import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Target, Menu, X, User, LogOut, Settings, Brain } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'

export default function ProfessionalHeader() {
  const { user, company, signOut } = useAuth()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false)
  const location = useLocation()

  const isActive = (path) => location.pathname === path

  const handleLogout = async () => {
    setProfileDropdownOpen(false)
    await signOut()
  }

  const getUserInitials = () => {
    if (user?.full_name) {
      return user.full_name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2)
    }
    return user?.email?.[0]?.toUpperCase() || '?'
  }

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/10 backdrop-blur-2xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            
            {/* Logo moderne */}
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-pink-500 to-blue-500 rounded-xl blur-lg opacity-50 group-hover:opacity-75 transition-opacity"></div>
                <div className="relative w-12 h-12 bg-gradient-to-r from-pink-500 to-blue-500 rounded-xl flex items-center justify-center shadow-lg">
                  <Brain className="w-7 h-7 text-white" />
                </div>
              </div>
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-pink-400 to-blue-400 bg-clip-text text-transparent">
                  Resume Screening Pro
                </h1>
                <p className="text-xs text-gray-400 -mt-1">IA Expert RH</p>
              </div>
            </Link>

            {/* Navigation Desktop */}
            <nav className="hidden md:flex space-x-2">
              <Link
                to="/"
                className={`px-6 py-3 rounded-xl text-sm font-medium transition-all duration-300 ${
                  isActive('/') 
                    ? 'bg-gradient-to-r from-pink-500 to-blue-500 text-white shadow-lg transform scale-105' 
                    : 'text-gray-300 hover:text-white hover:bg-white/10 hover:scale-105'
                }`}
              >
                Dashboard
              </Link>
              <Link
                to="/screening"
                className={`px-6 py-3 rounded-xl text-sm font-medium transition-all duration-300 ${
                  isActive('/screening') 
                    ? 'bg-gradient-to-r from-pink-500 to-blue-500 text-white shadow-lg transform scale-105' 
                    : 'text-gray-300 hover:text-white hover:bg-white/10 hover:scale-105'
                }`}
              >
                Screening
              </Link>
              <Link
                to="/settings"
                className={`px-6 py-3 rounded-xl text-sm font-medium transition-all duration-300 ${
                  isActive('/settings') 
                    ? 'bg-gradient-to-r from-pink-500 to-blue-500 text-white shadow-lg transform scale-105' 
                    : 'text-gray-300 hover:text-white hover:bg-white/10 hover:scale-105'
                }`}
              >
                Paramètres
              </Link>
            </nav>

            {/* Profile Menu - Position relative importante */}
            <div className="relative z-50">
              <button
                onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                className="flex items-center space-x-3 p-3 rounded-xl hover:bg-white/10 transition-all duration-300 group"
              >
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-pink-500 to-blue-500 rounded-full blur-lg opacity-50 group-hover:opacity-75 transition-opacity"></div>
                  <div className="relative w-10 h-10 bg-gradient-to-r from-pink-500 to-blue-500 rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-sm font-semibold text-white">
                      {getUserInitials()}
                    </span>
                  </div>
                </div>
                <div className="hidden sm:block text-left">
                  <div className="text-sm font-medium text-white group-hover:text-blue-300 transition-colors">
                    {user?.full_name || user?.email?.split('@')[0]}
                  </div>
                  <div className="text-xs text-gray-400">
                    {company?.name || 'Mon Entreprise'}
                  </div>
                </div>
              </button>

              {/* Dropdown - Position absolue corrigée */}
              {profileDropdownOpen && (
                <div 
                  className="absolute right-0 mt-3 w-72 bg-gray-900/95 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl overflow-hidden"
                  style={{ top: '100%' }}
                >
                  {/* Header du dropdown */}
                  <div className="px-6 py-4 bg-gradient-to-r from-pink-500/10 to-blue-500/10 border-b border-white/10">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-blue-500 rounded-full flex items-center justify-center">
                        <span className="text-lg font-bold text-white">
                          {getUserInitials()}
                        </span>
                      </div>
                      <div>
                        <div className="text-white font-medium">
                          {user?.full_name || user?.email}
                        </div>
                        <div className="text-sm text-gray-400">
                          {user?.email}
                        </div>
                        {company?.name && (
                          <div className="text-xs text-blue-400 mt-1">
                            🏢 {company.name}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Menu items */}
                  <div className="py-2">
                    <Link
                      to="/settings"
                      className="flex items-center space-x-3 px-6 py-3 text-gray-300 hover:text-white hover:bg-white/10 transition-all duration-200"
                      onClick={() => setProfileDropdownOpen(false)}
                    >
                      <User className="w-5 h-5" />
                      <span>Mon Profil</span>
                    </Link>
                    
                    <Link
                      to="/settings"
                      className="flex items-center space-x-3 px-6 py-3 text-gray-300 hover:text-white hover:bg-white/10 transition-all duration-200"
                      onClick={() => setProfileDropdownOpen(false)}
                    >
                      <Settings className="w-5 h-5" />
                      <span>Paramètres</span>
                    </Link>
                    
                    <hr className="my-2 border-white/10" />
                    
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center space-x-3 px-6 py-3 text-red-400 hover:text-red-300 hover:bg-red-900/20 transition-all duration-200"
                    >
                      <LogOut className="w-5 h-5" />
                      <span>Déconnexion</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-3 text-gray-300 hover:text-white hover:bg-white/10 rounded-xl transition-all"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden bg-black/40 backdrop-blur-lg rounded-xl p-4 mt-2 mb-4 border border-white/10">
              <div className="space-y-2">
                <Link
                  to="/"
                  className={`block px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                    isActive('/') 
                      ? 'bg-gradient-to-r from-pink-500 to-blue-500 text-white' 
                      : 'text-gray-300 hover:text-white hover:bg-white/10'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Dashboard
                </Link>
                <Link
                  to="/screening"
                  className={`block px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                    isActive('/screening') 
                      ? 'bg-gradient-to-r from-pink-500 to-blue-500 text-white' 
                      : 'text-gray-300 hover:text-white hover:bg-white/10'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Screening
                </Link>
                <Link
                  to="/settings"
                  className={`block px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                    isActive('/settings') 
                      ? 'bg-gradient-to-r from-pink-500 to-blue-500 text-white' 
                      : 'text-gray-300 hover:text-white hover:bg-white/10'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Paramètres
                </Link>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Overlay pour fermer le dropdown - Z-index plus bas */}
      {(profileDropdownOpen || mobileMenuOpen) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setProfileDropdownOpen(false)
            setMobileMenuOpen(false)
          }}
        />
      )}
    </>
  )
}